import React from "react";
import Calendar from "react-calendar";
import type { Value } from "react-calendar/dist/cjs/shared/types";
import { MdAccessTime, MdCalendarMonth } from "react-icons/md";
import "react-calendar/dist/Calendar.css";
import "assets/css/MiniCalendar.css";
import { useLanguage } from "context/LanguageContext";

type DateTimeInputProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  min?: string;
  max?: string;
  disabled?: boolean;
  extra?: string;
};

const DateTimeInput = ({
  id,
  label,
  value,
  onChange,
  required = false,
  min,
  max,
  disabled = false,
  extra = "",
}: DateTimeInputProps) => {
  const { t, locale } = useLanguage();
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [draftDate, setDraftDate] = React.useState<Date | null>(null);
  const [draftHour, setDraftHour] = React.useState("08");
  const [draftMinute, setDraftMinute] = React.useState("00");

  const parseInputValue = React.useCallback((input: string) => {
    if (!input) return null;
    const [datePart, timePart] = input.split("T");
    if (!datePart || !timePart) return null;

    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute] = timePart.split(":").map(Number);
    if ([year, month, day, hour, minute].some((item) => Number.isNaN(item))) {
      return null;
    }
    return new Date(year, month - 1, day, hour, minute, 0, 0);
  }, []);

  const buildInputValue = React.useCallback(
    (date: Date, hour: string, minute: string) => {
      const composed = new Date(date);
      composed.setHours(Number(hour), Number(minute), 0, 0);

      const year = composed.getFullYear();
      const month = String(composed.getMonth() + 1).padStart(2, "0");
      const day = String(composed.getDate()).padStart(2, "0");
      const hh = String(composed.getHours()).padStart(2, "0");
      const mm = String(composed.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hh}:${mm}`;
    },
    []
  );

  const isWithinRange = React.useCallback(
    (candidate: string) => {
      if (min && candidate < min) return false;
      if (max && candidate > max) return false;
      return true;
    },
    [max, min]
  );

  const displayValue = React.useMemo(() => {
    const parsed = parseInputValue(value);
    if (!parsed) return "";
    return parsed.toLocaleString(locale, {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }, [locale, parseInputValue, value]);

  React.useEffect(() => {
    if (!isOpen) return;

    const parsed = parseInputValue(value);
    if (parsed) {
      setDraftDate(
        new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
      );
      setDraftHour(String(parsed.getHours()).padStart(2, "0"));
      setDraftMinute(String(parsed.getMinutes()).padStart(2, "0"));
      return;
    }

    const now = new Date();
    setDraftDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
    setDraftHour(String(now.getHours()).padStart(2, "0"));
    setDraftMinute(String(now.getMinutes()).padStart(2, "0"));
  }, [isOpen, parseInputValue, value]);

  React.useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(event.target as Node)) return;
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const hourOptions = React.useMemo(
    () =>
      Array.from({ length: 24 }, (_, index) => String(index).padStart(2, "0")),
    []
  );

  const minuteOptions = React.useMemo(
    () =>
      Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0")),
    []
  );

  const applySelection = () => {
    if (!draftDate) return;
    const nextValue = buildInputValue(draftDate, draftHour, draftMinute);
    if (!isWithinRange(nextValue)) return;
    onChange(nextValue);
    setIsOpen(false);
  };

  const minDate = min ? parseInputValue(min) ?? undefined : undefined;
  const maxDate = max ? parseInputValue(max) ?? undefined : undefined;
  const isCandidateInRange = draftDate
    ? isWithinRange(buildInputValue(draftDate, draftHour, draftMinute))
    : true;

  return (
    <div className={extra} ref={wrapperRef}>
      <label
        htmlFor={id}
        className="mb-1.5 block text-sm font-medium text-ink-muted"
      >
        {label}
      </label>

      <input
        id={id}
        type="text"
        required={required}
        readOnly
        value={displayValue}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setIsOpen(true);
        }}
        placeholder={t("dateTimeInput.placeholder")}
        className="bg-transparent w-full cursor-pointer rounded-lg border border-line px-4 py-2.5 text-sm text-ink outline-none focus:border-brand-500 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-surface"
      />

      {/* keep native form required behavior with actual serialized value */}
      <input
        type="hidden"
        name={id}
        value={value}
        required={required}
        readOnly
      />

      {isOpen && !disabled && (
        <div className="absolute z-50 mt-2 w-full max-w-md rounded-2xl border border-line bg-surface p-4 shadow-2xl">
          <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
            <MdCalendarMonth className="h-4 w-4" />
            {t("dateTimeInput.pickDateFirst")}
          </div>

          <Calendar
            onChange={(selected: Value) => {
              if (selected instanceof Date) {
                setDraftDate(
                  new Date(
                    selected.getFullYear(),
                    selected.getMonth(),
                    selected.getDate()
                  )
                );
              }
            }}
            value={draftDate ?? undefined}
            minDate={minDate}
            maxDate={maxDate}
            view="month"
          />

          <div className="mt-4 border-t border-line pt-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
              <MdAccessTime className="h-4 w-4" />
              {t("dateTimeInput.thenPickTime")}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <select
                value={draftHour}
                disabled={!draftDate}
                onChange={(event) => setDraftHour(event.target.value)}
                className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {hourOptions.map((hour) => (
                  <option key={hour} value={hour}>
                    {t("dateTimeInput.hourOption", { hour })}
                  </option>
                ))}
              </select>

              <select
                value={draftMinute}
                disabled={!draftDate}
                onChange={(event) => setDraftMinute(event.target.value)}
                className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {minuteOptions.map((minute) => (
                  <option key={minute} value={minute}>
                    {t("dateTimeInput.minuteOption", { minute })}
                  </option>
                ))}
              </select>
            </div>

            {!isCandidateInRange && (
              <p className="mt-2 text-xs text-red-500">
                {t("dateTimeInput.outOfRange")}
              </p>
            )}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-lg border border-line px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-surface-hover dark:text-gray-300"
              >
                {t("action.cancel")}
              </button>
              <button
                type="button"
                onClick={applySelection}
                disabled={!draftDate || !isCandidateInRange}
                className="rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t("dateTimeInput.apply")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimeInput;
